// =============================================================================
// UserManagement.jsx - User Management Component with Subscription Limits
// =============================================================================
// Original layout preserved, subscription tier limits added
// Club ID display added for easy sharing
// Promote/Demote admin functionality added
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../utils/firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import PremiumFeatureModal from '../PremiumFeatureModal';
import TrialExpiredModal from '../TrialExpiredModal';

// Import subscription limits
import {
  getLimitsForTier,
  getUpgradeTiers,
  getUserLimitBenefits
} from '../../config/subscriptionTiers';

// =============================================================================
// HELPER FUNCTION: Format Date
// =============================================================================
const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// =============================================================================
// DEACTIVATE CONFIRMATION MODAL
// =============================================================================
const DeactivateModal = ({ user, onConfirm, onCancel }) => {
  if (!user) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'start',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div style={{
            flex: '0 0 auto',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 8px 0'
            }}>
              Deactivate User
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: 0,
              lineHeight: '1.5'
            }}>
              Are you sure you want to deactivate <strong>{user.email}</strong>? They will lose access to the club but their account data will be preserved.
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              color: '#374151'
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc2626',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              color: 'white'
            }}
          >
            Deactivate User
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// ROLE CHANGE CONFIRMATION MODAL
// =============================================================================
const RoleChangeModal = ({ user, action, onConfirm, onCancel, isLastAdmin }) => {
  if (!user) return null;

  const isPromoting = action === 'promote';
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'start',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div style={{
            flex: '0 0 auto',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: isPromoting ? '#dbeafe' : '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '20px' }}>{isPromoting ? '👑' : '👤'}</span>
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 8px 0'
            }}>
              {isPromoting ? 'Promote to Admin' : 'Demote to User'}
            </h3>
            {isLastAdmin ? (
              <p style={{
                fontSize: '14px',
                color: '#dc2626',
                margin: 0,
                lineHeight: '1.5'
              }}>
                <strong>Cannot demote {user.email}</strong> — they are the only admin. Your club must have at least one admin at all times.
              </p>
            ) : (
              <p style={{
                fontSize: '14px',
                color: '#6b7280',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {isPromoting ? (
                  <>Are you sure you want to promote <strong>{user.email}</strong> to admin? They will be able to manage users, teams, pitches, and club settings.</>
                ) : (
                  <>Are you sure you want to demote <strong>{user.email}</strong> to a regular user? They will lose admin privileges and can only view allocations.</>
                )}
              </p>
            )}
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              color: '#374151'
            }}
          >
            Cancel
          </button>
          {!isLastAdmin && (
            <button
              onClick={onConfirm}
              style={{
                padding: '10px 20px',
                backgroundColor: isPromoting ? '#2563eb' : '#d97706',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: 'white'
              }}
            >
              {isPromoting ? 'Promote to Admin' : 'Demote to User'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const UserManagement = ({ 
  clubInfo, 
  showSuccess, 
  isAdmin,
  subscriptionTier = 'free'
}) => {
  // ===========================================================================
  // STATE
  // ===========================================================================
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [deactivatedUsers, setDeactivatedUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [errors, setErrors] = useState({});
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [copiedClubId, setCopiedClubId] = useState(false);
  
  // Role change modal state
  const [userToChangeRole, setUserToChangeRole] = useState(null);
  const [roleChangeAction, setRoleChangeAction] = useState(null); // 'promote' or 'demote'
  const [showRoleChangeModal, setShowRoleChangeModal] = useState(false);
  
  // Premium modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalConfig, setPremiumModalConfig] = useState({});
  
  // Trial expired modal state
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  // ===========================================================================
  // TRIAL CHECK
  // ===========================================================================
  const isTrialExpired = () => {
    if (!clubInfo) return false;
    
    const now = new Date();
    const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
      clubInfo.trialEndsAt.toDate() : 
      new Date(clubInfo.trialEndsAt);
    
    const subscriptionStatus = clubInfo.subscriptionStatus || 'trial';
    const msRemaining = trialEndsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    
    return subscriptionStatus === 'trial' && daysRemaining <= 0;
  };

  // ===========================================================================
  // SUBSCRIPTION LIMITS
  // ===========================================================================
  const limits = getLimitsForTier(subscriptionTier);
  
  // Check for club-specific admin override
  const adminOverride = clubInfo?.adminUserOverride || 0;
  const effectiveMaxAdmins = limits.maxAdmins + adminOverride;
  
  // Calculate current counts
  const activeAdmins = activeUsers.filter(u => u.role === 'admin').length;
  const activeRegularUsers = activeUsers.filter(u => u.role === 'user').length;
  const totalActiveUsers = activeAdmins + activeRegularUsers;
  
  // Check if can add more users
  const canAddMoreAdmins = activeAdmins < effectiveMaxAdmins;
  const canAddMoreUsers = activeRegularUsers < limits.maxUsers;
  const canAddMoreTotal = totalActiveUsers < (limits.totalUsersAllowed + adminOverride);

  // Check if there's only one admin (for preventing last admin demotion)
  const isLastAdmin = activeAdmins <= 1;

  // Check if specific user can be reactivated
  const [userToReactivate, setUserToReactivate] = useState(null);

  // ===========================================================================
  // LOAD USERS ON EXPAND
  // ===========================================================================
  useEffect(() => {
    if (isExpanded && clubInfo?.clubId) {
      loadUsers();
    }
  }, [isExpanded, clubInfo?.clubId]);

  const loadUsers = async () => {
    if (!clubInfo?.clubId) return;

    setIsLoadingUsers(true);
    setErrors({});
    
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('clubId', '==', clubInfo.clubId)
      );
      
      const querySnapshot = await getDocs(usersQuery);
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const active = usersList.filter(u => u.status === 'active' || !u.status);
      const deactivated = usersList.filter(u => u.status === 'deactivated');

      setActiveUsers(active);
      setDeactivatedUsers(deactivated);
    } catch (error) {
      console.error('Error loading users:', error);
      setErrors({ users: 'Failed to load users. Please try again.' });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // ===========================================================================
  // HANDLERS: Deactivate User
  // ===========================================================================
  const handleDeactivateClick = (user) => {
    setUserToDeactivate(user);
    setShowDeactivateModal(true);
  };

  const confirmDeactivate = async () => {
    if (!userToDeactivate) return;

    try {
      const userRef = doc(db, 'users', userToDeactivate.id);
      await updateDoc(userRef, {
        status: 'deactivated',
        deactivatedAt: new Date().toISOString()
      });
      
      setActiveUsers(activeUsers.filter(u => u.id !== userToDeactivate.id));
      setDeactivatedUsers([
        ...deactivatedUsers,
        {
          ...userToDeactivate,
          status: 'deactivated',
          deactivatedAt: new Date().toISOString()
        }
      ]);
      
      showSuccess(`User ${userToDeactivate.email} has been deactivated`);
    } catch (error) {
      console.error('Error deactivating user:', error);
      setErrors({ users: 'Failed to deactivate user. Please try again.' });
    } finally {
      setShowDeactivateModal(false);
      setUserToDeactivate(null);
    }
  };

  // ===========================================================================
  // HANDLERS: Role Change (Promote/Demote)
  // ===========================================================================
  const handleRoleChangeClick = (user) => {
    const action = user.role === 'admin' ? 'demote' : 'promote';
    setUserToChangeRole(user);
    setRoleChangeAction(action);
    setShowRoleChangeModal(true);
  };

  const confirmRoleChange = async () => {
    if (!userToChangeRole || !roleChangeAction) return;

    const isPromoting = roleChangeAction === 'promote';
    const newRole = isPromoting ? 'admin' : 'user';

    // Double-check: prevent demoting the last admin
    if (!isPromoting && isLastAdmin && userToChangeRole.role === 'admin') {
      setErrors({ users: 'Cannot demote the last admin. Promote another user first.' });
      setShowRoleChangeModal(false);
      setUserToChangeRole(null);
      setRoleChangeAction(null);
      return;
    }

    // Check subscription limits when promoting
    if (isPromoting && !canAddMoreAdmins) {
      const upgradeTiers = getUpgradeTiers(subscriptionTier, 'admins');
      setPremiumModalConfig({
        featureName: 'Additional Admin Accounts',
        currentTier: subscriptionTier,
        requiredTiers: upgradeTiers.map(t => t.toLowerCase()),
        benefits: getUserLimitBenefits(upgradeTiers)
      });
      setShowPremiumModal(true);
      setShowRoleChangeModal(false);
      setUserToChangeRole(null);
      setRoleChangeAction(null);
      return;
    }

    try {
      const userRef = doc(db, 'users', userToChangeRole.id);
      await updateDoc(userRef, {
        role: newRole,
        roleChangedAt: new Date().toISOString()
      });
      
      // Update local state
      setActiveUsers(activeUsers.map(u => 
        u.id === userToChangeRole.id 
          ? { ...u, role: newRole, roleChangedAt: new Date().toISOString() }
          : u
      ));
      
      const actionWord = isPromoting ? 'promoted to admin' : 'demoted to user';
      showSuccess(`${userToChangeRole.email} has been ${actionWord}`);
    } catch (error) {
      console.error('Error changing user role:', error);
      setErrors({ users: 'Failed to change user role. Please try again.' });
    } finally {
      setShowRoleChangeModal(false);
      setUserToChangeRole(null);
      setRoleChangeAction(null);
    }
  };

  // ===========================================================================
  // HANDLERS: Copy Club ID
  // ===========================================================================
  const handleCopyClubId = async () => {
    if (!clubInfo?.clubId) return;
    
    try {
      await navigator.clipboard.writeText(clubInfo.clubId);
      setCopiedClubId(true);
      showSuccess('Club ID copied to clipboard!');
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedClubId(false);
      }, 2000);
    } catch (error) {
      console.error('Error copying club ID:', error);
      setErrors({ clubId: 'Failed to copy club ID. Please try again.' });
    }
  };

  // ===========================================================================
  // HANDLERS: Reactivate User (with limit check)
  // ===========================================================================
  const handleReactivate = async (user) => {
    setUserToReactivate(user);

    // Check role-specific limits
    const willExceedRoleLimit = user.role === 'admin' 
      ? !canAddMoreAdmins 
      : !canAddMoreUsers;

    if (willExceedRoleLimit) {
      const limitType = user.role === 'admin' ? 'admins' : 'users';
      const upgradeTiers = getUpgradeTiers(subscriptionTier, limitType);
      setPremiumModalConfig({
        featureName: `Additional ${user.role === 'admin' ? 'Admin' : 'User'} Accounts`,
        currentTier: subscriptionTier,
        requiredTiers: upgradeTiers.map(t => t.toLowerCase()),
        benefits: getUserLimitBenefits(upgradeTiers)
      });
      setShowPremiumModal(true);
      return;
    }

    if (!canAddMoreTotal) {
      const upgradeTiers = getUpgradeTiers(subscriptionTier, 'users');
      setPremiumModalConfig({
        featureName: 'Additional User Accounts',
        currentTier: subscriptionTier,
        requiredTiers: upgradeTiers.map(t => t.toLowerCase()),
        benefits: getUserLimitBenefits(upgradeTiers)
      });
      setShowPremiumModal(true);
      return;
    }

    // Proceed with reactivation
    try {
      const userRef = doc(db, 'users', userToReactivate.id);
      await updateDoc(userRef, {
        status: 'active',
        deactivatedAt: null
      });
      
      setDeactivatedUsers(deactivatedUsers.filter(u => u.id !== userToReactivate.id));
      
      const { deactivatedAt, ...userWithoutDeactivation } = userToReactivate;
      setActiveUsers([
        ...activeUsers,
        {
          ...userWithoutDeactivation,
          status: 'active'
        }
      ]);
      
      showSuccess(`User ${userToReactivate.email} has been reactivated`);
    } catch (error) {
      console.error('Error reactivating user:', error);
      setErrors({ users: 'Failed to reactivate user. Please try again.' });
    }
  };

  // ===========================================================================
  // RENDER: Only show to admins
  // ===========================================================================
  if (!isAdmin) return null;

  const isPremiumTier = subscriptionTier === 'premium';

  return (
    <>
      {/* User Management Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {/* Header - Always Visible */}
        <div
          onClick={() => {
            if (isTrialExpired()) {
              setShowTrialExpiredModal(true);
              return;
            }
            setIsExpanded(!isExpanded);
          }}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px',
            margin: '-8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              User Management
            </h2>
            
            {!isLoadingUsers && isExpanded && (
              <span style={{
                fontSize: '14px',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                padding: '2px 8px',
                borderRadius: '4px'
              }}>
                {activeUsers.length + deactivatedUsers.length} total • {activeUsers.length} active
              </span>
            )}
          </div>

          <div style={{
            fontSize: '20px',
            color: '#6b7280',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </div>
        </div>

        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
          Manage user accounts, roles, and permissions for your club
        </p>

        {/* Expanded Content */}
        {isExpanded && (
          <div style={{ marginTop: '24px' }}>
            {/* Club ID Display */}
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px'
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#15803d',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Club Join Code
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flex: 1
                }}>
                  <span style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#166534',
                    letterSpacing: '2px',
                    fontFamily: 'monospace'
                  }}>
                    {clubInfo?.clubId || 'N/A'}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyClubId();
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: copiedClubId ? '#22c55e' : 'white',
                    border: copiedClubId ? '1px solid #22c55e' : '1px solid #86efac',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: copiedClubId ? 'white' : '#15803d',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {copiedClubId ? '✓ Copied!' : '📋 Copy Code'}
                </button>
              </div>
              <div style={{
                fontSize: '11px',
                color: '#15803d',
                marginTop: '6px',
                fontStyle: 'italic'
              }}>
                Share this code with users to join your club
              </div>
            </div>

            {/* Subscription Limit Info */}
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: canAddMoreTotal ? '#f0f9ff' : '#fef3c7',
              borderRadius: '6px',
              fontSize: '13px',
              color: canAddMoreTotal ? '#0369a1' : '#d97706'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>{totalActiveUsers} of {limits.totalUsersAllowed + adminOverride} total users</strong> on your {limits.displayName} plan
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                • {activeAdmins} of {effectiveMaxAdmins} admin{effectiveMaxAdmins !== 1 ? 's' : ''}
                {' • '}
                {activeRegularUsers} of {limits.maxUsers} regular user{limits.maxUsers !== 1 ? 's' : ''}
              </div>
              {!canAddMoreTotal && (
                <div style={{ marginTop: '8px', fontWeight: '600' }}>
                  ⚠️ User limit reached - upgrade to add more users
                </div>
              )}
            </div>

            {/* Error Message */}
            {errors.users && (
              <div style={{
                padding: '12px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '14px',
                marginBottom: '16px'
              }}>
                {errors.users}
              </div>
            )}

            {/* Loading State */}
            {isLoadingUsers ? (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#6b7280'
              }}>
                Loading users...
              </div>
            ) : (
              <>
                {/* Active Users */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '16px'
                  }}>
                    Active Users ({activeUsers.length})
                  </h3>
                  
                  {activeUsers.length === 0 ? (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      color: '#6b7280'
                    }}>
                      No active users found
                    </div>
                  ) : (
                    <div style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      {activeUsers.map((user, index) => {
                        const isUserAdmin = user.role === 'admin';
                        const canDemote = !isLastAdmin || !isUserAdmin;
                        const canPromote = canAddMoreAdmins;
                        
                        return (
                          <div
                            key={user.id}
                            style={{
                              padding: '16px',
                              backgroundColor: 'white',
                              borderBottom: index < activeUsers.length - 1 ? '1px solid #e5e7eb' : 'none',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '500',
                                color: '#1f2937',
                                marginBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flexWrap: 'wrap'
                              }}>
                                <span>{user.email}</span>
                                {(user.firstName || user.lastName) && (
                                  <span style={{
                                    color: '#6b7280',
                                    fontWeight: '400'
                                  }}>
                                    ({[user.firstName, user.lastName].filter(Boolean).join(' ')})
                                  </span>
                                )}
                              </div>
                              <div style={{
                                display: 'flex',
                                gap: '12px',
                                fontSize: '13px',
                                color: '#6b7280'
                              }}>
                                <span style={{
                                  backgroundColor: isUserAdmin ? '#dbeafe' : '#f3f4f6',
                                  color: isUserAdmin ? '#1e40af' : '#374151',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontWeight: '500'
                                }}>
                                  {isUserAdmin ? 'Admin' : 'User'}
                                </span>
                                <span>Last login: {formatDate(user.lastLogin)}</span>
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', gap: '8px' }}>
                              {/* Promote/Demote Button */}
                              <button
                                onClick={() => handleRoleChangeClick(user)}
                                disabled={isUserAdmin && isLastAdmin}
                                title={
                                  isUserAdmin && isLastAdmin 
                                    ? 'Cannot demote the last admin' 
                                    : isUserAdmin 
                                      ? 'Demote to user' 
                                      : 'Promote to admin'
                                }
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: isUserAdmin 
                                    ? (isLastAdmin ? '#f3f4f6' : '#fffbeb') 
                                    : '#eff6ff',
                                  border: isUserAdmin 
                                    ? (isLastAdmin ? '1px solid #d1d5db' : '1px solid #fcd34d') 
                                    : '1px solid #93c5fd',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  color: isUserAdmin 
                                    ? (isLastAdmin ? '#9ca3af' : '#d97706') 
                                    : '#2563eb',
                                  cursor: (isUserAdmin && isLastAdmin) ? 'not-allowed' : 'pointer',
                                  fontWeight: '500',
                                  opacity: (isUserAdmin && isLastAdmin) ? 0.6 : 1
                                }}
                              >
                                {isUserAdmin ? 'Demote' : 'Promote'}
                              </button>
                              
                              {/* Deactivate Button */}
                              <button
                                onClick={() => handleDeactivateClick(user)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: 'white',
                                  border: '1px solid #fca5a5',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  color: '#dc2626',
                                  cursor: 'pointer',
                                  fontWeight: '500'
                                }}
                              >
                                Deactivate
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Deactivated Users */}
                {deactivatedUsers.length > 0 && (
                  <div>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '16px'
                    }}>
                      Deactivated Users ({deactivatedUsers.length})
                    </h3>
                    
                    <div style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      {deactivatedUsers.map((user, index) => (
                        <div
                          key={user.id}
                          style={{
                            padding: '16px',
                            backgroundColor: '#fafafa',
                            borderBottom: index < deactivatedUsers.length - 1 ? '1px solid #e5e7eb' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '500',
                              color: '#6b7280',
                              marginBottom: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              flexWrap: 'wrap'
                            }}>
                              <span>{user.email}</span>
                              {(user.firstName || user.lastName) && (
                                <span style={{
                                  color: '#9ca3af',
                                  fontWeight: '400'
                                }}>
                                  ({[user.firstName, user.lastName].filter(Boolean).join(' ')})
                                </span>
                              )}
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: '12px',
                              fontSize: '13px',
                              color: '#9ca3af'
                            }}>
                              <span style={{
                                backgroundColor: '#e5e7eb',
                                color: '#6b7280',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontWeight: '500'
                              }}>
                                {user.role === 'admin' ? 'Admin' : 'User'}
                              </span>
                              <span>Deactivated: {formatDate(user.deactivatedAt)}</span>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => handleReactivate(user)}
                            disabled={!canAddMoreTotal}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: canAddMoreTotal ? '#dcfce7' : '#f3f4f6',
                              border: canAddMoreTotal ? '1px solid #86efac' : '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '13px',
                              color: canAddMoreTotal ? '#15803d' : '#9ca3af',
                              cursor: canAddMoreTotal ? 'pointer' : 'not-allowed',
                              fontWeight: '500'
                            }}
                          >
                            {canAddMoreTotal ? 'Reactivate' : 'Limit Reached'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Deactivate Confirmation Modal */}
      {showDeactivateModal && (
        <DeactivateModal
          user={userToDeactivate}
          onConfirm={confirmDeactivate}
          onCancel={() => {
            setShowDeactivateModal(false);
            setUserToDeactivate(null);
          }}
        />
      )}

      {/* Role Change Confirmation Modal */}
      {showRoleChangeModal && (
        <RoleChangeModal
          user={userToChangeRole}
          action={roleChangeAction}
          isLastAdmin={isLastAdmin && userToChangeRole?.role === 'admin'}
          onConfirm={confirmRoleChange}
          onCancel={() => {
            setShowRoleChangeModal(false);
            setUserToChangeRole(null);
            setRoleChangeAction(null);
          }}
        />
      )}

      {/* Premium Feature Modal */}
      {showPremiumModal && (
        <PremiumFeatureModal
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          {...premiumModalConfig}
        />
      )}

      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
      />
    </>
  );
};

export default UserManagement;
